import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { ArrowBack, Payment, TextFields, Image } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { Campaign } from '../types';
import StatisticsCharts from '../components/StatisticsCharts';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import CampaignStatusChip from '../components/CampaignStatusChip';
import { normalizeImageUrl } from '../utils/imageUtils';

const CampaignDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useTelegramWebApp();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [banners, setBanners] = useState<any[]>([]);
  const [creatives, setCreatives] = useState<any[]>([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [hasYandexImageAds, setHasYandexImageAds] = useState(false);
  const [hasYandexTextAds, setHasYandexTextAds] = useState(false);
  // Удалены неиспользуемые переменные
  
  // Отдельные состояния для генерации баннеров и текстовых объявлений
  const [isGeneratingBanners, setIsGeneratingBanners] = useState(false);
  const [isGeneratingTextCreatives, setIsGeneratingTextCreatives] = useState(false);
  const [bannerGenerationError, setBannerGenerationError] = useState<string>('');
  const [textGenerationError, setTextGenerationError] = useState<string>('');
  
  // Состояния для генерации дополнительных баннеров
  const [isGeneratingMoreBanners, setIsGeneratingMoreBanners] = useState(false);
  const [generatedBannersCount, setGeneratedBannersCount] = useState(0);
  
  // Прогресс для баннеров
  const [bannerProgress, setBannerProgress] = useState(0);
  const [bannerCurrentTask, setBannerCurrentTask] = useState('');
  
  // Прогресс для текстовых объявлений
  const [textProgress, setTextProgress] = useState(0);
  const [textCurrentTask, setTextCurrentTask] = useState('');
  
  // Состояния для отправки на модерацию в Яндекс.Директ
  const [isSendingToModeration, setIsSendingToModeration] = useState(false);
  const [moderationProgress, setModerationProgress] = useState(0);
  const [moderationCurrentTask, setModerationCurrentTask] = useState('');
  const [moderationError, setModerationError] = useState<string>('');
  const [moderationSuccess, setModerationSuccess] = useState(false);
  
  const [showKeywords, setShowKeywords] = useState(false);
  const [showNegatives, setShowNegatives] = useState(false);

  // Функция для преобразования объявлений text_keywords в формат объявлений
  const getTextKeywordsAds = () => {
    const textKeywordsCreatives = creatives.filter(creative => creative.type === 'text_keywords');
    const allAds: any[] = [];
    
    textKeywordsCreatives.forEach(creative => {
      try {
        const content = JSON.parse(creative.content);
        if (content.ads && Array.isArray(content.ads)) {
          content.ads.forEach((ad: any, index: number) => {
            allAds.push({
              id: `creative-${creative.id}-${index}`,
              title: ad.header || 'Объявление',
              text: ad.text || '',
              href: ad.links && ad.links.length > 0 ? ad.links[0].url : campaign?.campaign_data?.site_url || '',
              status: 'active',
              status_name: 'Активно',
              created_at: creative.created_at,
              isFromCreative: true,
              creative_id: creative.id,
              links: ad.links || [],
              details: ad.details || []
            });
          });
        }
      } catch (e) {
        console.error('Failed to parse creative content:', e);
      }
    });
    
    return allAds;
  };

  // Функция для извлечения ключевых слов из объявлений
  const getKeywords = () => {
    const textKeywordsCreatives = creatives.filter(creative => creative.type === 'text_keywords');
    const allKeywords: string[] = [];
    
    textKeywordsCreatives.forEach(creative => {
      try {
        const content = JSON.parse(creative.content);
        if (content.keywords && Array.isArray(content.keywords)) {
          allKeywords.push(...content.keywords);
        }
      } catch (e) {
        console.error('Failed to parse creative content for keywords:', e);
      }
    });
    
    return [...new Set(allKeywords)]; // Убираем дубликаты
  };

  // Функция для извлечения минус-слов из объявлений
  const getNegatives = () => {
    const textKeywordsCreatives = creatives.filter(creative => creative.type === 'text_keywords');
    const allNegatives: string[] = [];
    
    textKeywordsCreatives.forEach(creative => {
      try {
        const content = JSON.parse(creative.content);
        if (content.negatives && Array.isArray(content.negatives)) {
          allNegatives.push(...content.negatives);
        }
      } catch (e) {
        console.error('Failed to parse creative content for negatives:', e);
      }
    });
    
    return [...new Set(allNegatives)]; // Убираем дубликаты
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!id) {
        setError('ID кампании не указан');
        setLoading(false);
        return;
      }

      try {
        const campaignData = await apiService.getCampaign(parseInt(id));
        setCampaign(campaignData);
      } catch (err: any) {
        setError(err.message || 'Не удалось загрузить кампанию');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id]);

  useEffect(() => {
    const fetchBannersAndAds = async () => {
      if (!campaign?.id || !user?.id) return;
      
      // Загружаем баннеры
      setBannersLoading(true);
      try {
        const bannersResult = await apiService.getCampaignBanners(campaign.id);
        const loadedBanners = bannersResult.banners || [];
        setBanners(loadedBanners);
        
        // Инициализируем счетчик сгенерированных баннеров (как в онбординге)
        // Считаем все баннеры как сгенерированные
        setGeneratedBannersCount(loadedBanners.length);
      } catch (err: any) {
        console.error('Failed to load banners:', err);
        setBanners([]);
        setGeneratedBannersCount(0);
      } finally {
        setBannersLoading(false);
      }

      // Загружаем данные о Яндекс.Директ объявлениях
      try {
        const adsResult = await apiService.getCampaignAds(campaign.id);
        setHasYandexImageAds(adsResult.has_yandex_image_ads || false);
        setHasYandexTextAds(adsResult.has_yandex_text_ads || false);
      } catch (err: any) {
        console.error('Failed to load Yandex ads status:', err);
        setHasYandexImageAds(false);
        setHasYandexTextAds(false);
      }

      // Загружаем объявления
      setCreativesLoading(true);
      try {
        const creativesResult = await apiService.getCampaignCreatives(campaign.id, user.id);
        setCreatives(creativesResult.creatives || []);
      } catch (err: any) {
        console.error('Failed to load creatives:', err);
        setCreatives([]);
      } finally {
        setCreativesLoading(false);
      }
    };

    fetchBannersAndAds();
  }, [campaign?.id, user?.id]);

  // Инициализируем счетчик баннеров (как в онбординге)
  useEffect(() => {
    if (banners.length > 0 && generatedBannersCount === 0) {
      setGeneratedBannersCount(banners.length);
    }
  }, [banners.length, generatedBannersCount]);

  const handleGoBack = () => {
    navigate('/campaigns');
  };

  const handlePayCampaign = () => {
    if (campaign?.budget && campaign?.id) {
      const params = new URLSearchParams();
      params.append('amount', campaign.budget.toString());
      params.append('campaign_id', campaign.id.toString());
      navigate(`/topup?${params.toString()}`);
    }
  };

  // Обработчик для генерации баннеров
  const handleGenerateBanners = async () => {
    if (!campaign?.id || !user?.id || isGeneratingBanners) return;

    setIsGeneratingBanners(true);
    setBannerGenerationError('');
    setBannerProgress(10);
    setBannerCurrentTask('Запускаем генерацию баннеров...');

    try {
      console.log('🎨 Starting banner generation for campaign:', campaign.id);
      
      // Имитируем прогресс
      const progressInterval = setInterval(() => {
        setBannerProgress(prev => {
          if (prev < 90) return prev + Math.random() * 10;
          return prev;
        });
      }, 2000);

      const taskInterval = setInterval(() => {
        const tasks = [
          'Анализируем дизайн...',
          'Генерируем концепции...',
          'Создаем изображения...',
          'Оптимизируем баннеры...',
          'Сохраняем результаты...'
        ];
        setBannerCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
      }, 3000);
      
      const result = await apiService.generateOnboardingGraphicCreatives({
        telegram_user_id: user.id,
        campaign_id: campaign.id
      });

      // Очищаем интервалы
      clearInterval(progressInterval);
      clearInterval(taskInterval);

      console.log('✅ Banner generation successful', result);
      
      // Завершаем прогресс
      setBannerProgress(100);
      setBannerCurrentTask('Баннеры готовы!');
      
      // Обновляем баннеры
      setTimeout(async () => {
        try {
          const bannersResult = await apiService.getCampaignBanners(campaign.id);
          setBanners(bannersResult.banners || []);
        } catch (err) {
          console.error('Failed to reload banners:', err);
        }
        
        setIsGeneratingBanners(false);
        setBannerProgress(0);
        setBannerCurrentTask('');
      }, 1500);
      
    } catch (error: any) {
      console.error('❌ Banner generation error:', error);
      setBannerGenerationError(error?.message || 'Не удалось сгенерировать баннеры. Попробуйте еще раз.');
      setIsGeneratingBanners(false);
      setBannerProgress(0);
      setBannerCurrentTask('');
    }
  };

  // Обработчик для генерации дополнительных баннеров (аналогично онбордингу)
  const handleGenerateMoreBanners = async () => {
    if (generatedBannersCount >= 3 || isGeneratingMoreBanners || !user?.id || !campaign?.id) {
      return;
    }

    setIsGeneratingMoreBanners(true);
    
    try {
      console.log('🎨 Generating additional banner, count:', generatedBannersCount);
      
      // Получаем первый баннер для использования его концепта
      const firstBanner = banners[0];
      if (!firstBanner?.concept_name) {
        throw new Error('Не найден концепт для генерации дополнительного баннера');
      }

      // Используем новый API для генерации и сохранения баннера
      const result = await apiService.generateAdditionalBanner({
        telegram_user_id: user.id,
        campaign_id: campaign.id,
        concept_name: firstBanner.concept_name,
        prompt: firstBanner.concept_name // Используем concept_name как prompt
      });

      if (result?.banner) {
        // Добавляем новый баннер в список
        setBanners(prev => [...prev, result.banner]);
        setGeneratedBannersCount(prev => prev + 1);
        
        console.log('✅ Additional banner generated and saved successfully', result);
      }
    } catch (error: any) {
      console.error('❌ Additional banner generation error:', error);
      setBannerGenerationError(error?.message || 'Не удалось сгенерировать дополнительный баннер');
    } finally {
      setIsGeneratingMoreBanners(false);
    }
  };

  // Обработчик для генерации текстовых объявлений (ключевые слова и объявления)
  const handleGenerateTextCreatives = async () => {
    if (!campaign?.id || !user?.id || isGeneratingTextCreatives) return;

    setIsGeneratingTextCreatives(true);
    setTextGenerationError('');
    setTextProgress(10);
    setTextCurrentTask('Создаем объявления...');

    try {
      console.log('📝 Starting text creatives generation for campaign:', campaign.id);
      
      // Имитируем прогресс
      const progressInterval = setInterval(() => {
        setTextProgress(prev => {
          if (prev < 90) return prev + Math.random() * 10;
          return prev;
        });
      }, 2000);

      const taskInterval = setInterval(() => {
        const tasks = [
          'Анализируем сайт...',
          'Генерируем ключевые слова...',
          'Создаем объявления...',
          'Подбираем минус-слова...',
          'Оптимизируем тексты...',
          'Сохраняем результаты...'
        ];
        setTextCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
      }, 3000);
      
      const result = await apiService.generateOnboardingTextCreatives({
        telegram_user_id: user.id,
        url: campaign.campaign_data?.site_url || '',
        goal: campaign.goal || 'sales',
        audience: 'Целевая аудитория',
        geo: campaign.geo || 'russia',
        campaign_id: campaign.id
      });

      // Очищаем интервалы
      clearInterval(progressInterval);
      clearInterval(taskInterval);

      console.log('✅ Text creatives generation successful', result);
      
      // Завершаем прогресс
      setTextProgress(100);
      setTextCurrentTask('Креативы готовы!');
      
      // Обновляем объявления
      setTimeout(async () => {
        try {
          const creativesResult = await apiService.getCampaignCreatives(campaign.id, user.id);
          setCreatives(creativesResult.creatives || []);
        } catch (err) {
          console.error('Failed to reload text creatives:', err);
        }
        
        setIsGeneratingTextCreatives(false);
        setTextProgress(0);
        setTextCurrentTask('');
      }, 1500);
      
    } catch (error: any) {
      console.error('❌ Text creatives generation error:', error);
      setTextGenerationError(error?.message || 'Не удалось создать текстовые объявления. Попробуйте еще раз.');
      setIsGeneratingTextCreatives(false);
      setTextProgress(0);
      setTextCurrentTask('');
    }
  };

  // Обработчик для отправки баннеров на модерацию в Яндекс.Директ
  const handleSendBannersToModeration = async () => {
    if (!campaign?.id || !user?.id || isSendingToModeration) return;

    setIsSendingToModeration(true);
    setModerationError('');
    setModerationSuccess(false);
    setModerationProgress(10);
    setModerationCurrentTask('Подготавливаем отправку баннеров на модерацию...');

    try {
      console.log('📤 Starting banners moderation submission for campaign:', campaign.id);
      
      // Имитируем прогресс
      const progressInterval = setInterval(() => {
        setModerationProgress(prev => {
          if (prev < 90) return prev + Math.random() * 10;
          return prev;
        });
      }, 2000);

      const taskInterval = setInterval(() => {
        const tasks = [
          'Загружаем изображения в Яндекс.Директ...',
          'Создаем объявления с изображениями...',
          'Отправляем на модерацию...',
          'Проверяем статус...'
        ];
        setModerationCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
      }, 3000);
      
      const result = await apiService.sendBannersToYandexModeration(campaign.id, user.id);

      // Очищаем интервалы
      clearInterval(progressInterval);
      clearInterval(taskInterval);

      console.log('✅ Banners moderation submission successful', result);
      
      // Завершаем прогресс
      setModerationProgress(100);
      setModerationCurrentTask('Баннеры отправлены на модерацию!');
      setModerationSuccess(true);
      
      // Обновляем данные кампании
      setTimeout(async () => {
        try {
          const campaignData = await apiService.getCampaign(campaign.id);
          setCampaign(campaignData);
          
          // Обновляем статус Яндекс.Директ баннеров
          const adsResult = await apiService.getCampaignAds(campaign.id);
          setHasYandexImageAds(adsResult.has_yandex_image_ads || false);
        } catch (err) {
          console.error('Failed to reload campaign data:', err);
        }
        
        setIsSendingToModeration(false);
        setModerationProgress(0);
        setModerationCurrentTask('');
        
        // Прокручиваем вверх страницы, чтобы показать уведомление об успехе
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 2000);

      
    } catch (error: any) {
      console.error('❌ Banners moderation submission error:', error);
      setModerationError(error?.message || 'Не удалось отправить баннеры на модерацию. Попробуйте еще раз.');
      setIsSendingToModeration(false);
      setModerationProgress(0);
      setModerationCurrentTask('');
    }
  };

  // Обработчик для отправки текстовых объявлений на модерацию в Яндекс.Директ
  const handleSendTextAdsToModeration = async () => {
    if (!campaign?.id || !user?.id || isSendingToModeration) return;

    setIsSendingToModeration(true);
    setModerationError('');
    setModerationSuccess(false);
    setModerationProgress(10);
    setModerationCurrentTask('Подготавливаем отправку текстовых объявлений на модерацию...');

    try {
      console.log('📤 Starting text ads moderation submission for campaign:', campaign.id);
      
      // Имитируем прогресс
      const progressInterval = setInterval(() => {
        setModerationProgress(prev => {
          if (prev < 90) return prev + Math.random() * 10;
          return prev;
        });
      }, 2000);

      const taskInterval = setInterval(() => {
        const tasks = [
          'Создаем текстовые объявления...',
          'Отправляем на модерацию...',
          'Проверяем статус...'
        ];
        setModerationCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
      }, 3000);
      
      const result = await apiService.sendTextAdsToYandexModeration(campaign.id, user.id);

      // Очищаем интервалы
      clearInterval(progressInterval);
      clearInterval(taskInterval);

      console.log('✅ Text ads moderation submission successful', result);
      
      // Завершаем прогресс
      setModerationProgress(100);
      setModerationCurrentTask('Текстовые объявления отправлены на модерацию!');
      setModerationSuccess(true);
      
      // Обновляем данные кампании
      setTimeout(async () => {
        try {
          const campaignData = await apiService.getCampaign(campaign.id);
          setCampaign(campaignData);
          
          // Обновляем статус Яндекс.Директ текстовых объявлений
          const adsResult = await apiService.getCampaignAds(campaign.id);
          setHasYandexTextAds(adsResult.has_yandex_text_ads || false);
        } catch (err) {
          console.error('Failed to reload campaign data:', err);
        }
        
        setIsSendingToModeration(false);
        setModerationProgress(0);
        setModerationCurrentTask('');
        
        // Прокручиваем вверх страницы, чтобы показать уведомление об успехе
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 2000);

      
    } catch (error: any) {
      console.error('❌ Text ads moderation submission error:', error);
      setModerationError(error?.message || 'Не удалось отправить на модерацию. Попробуйте еще раз.');
      setIsSendingToModeration(false);
      setModerationProgress(0);
      setModerationCurrentTask('');
    }
  };



  // Проверяем, нужно ли показывать кнопку "Отправить на модерацию"
  const shouldShowModerationButton = () => {
    // Показываем кнопку если:
    // 1. Есть текстовые объявления (объявления из объявлений)
    // 2. Создана кампания в Яндекс.Директ (есть yandex_campaign_id)
    // 3. Не идет процесс отправки на модерацию
    // 4. Статус кампании = 'draft' (черновик)
    const hasTextCreatives = getTextKeywordsAds().length > 0;
    const hasYandexCampaign = !!campaign?.yandex_campaign_id;
    const notCurrentlySending = !isSendingToModeration;
    const isDraftStatus = campaign?.status === 'draft';
    
    return hasTextCreatives && hasYandexCampaign && notCurrentlySending && isDraftStatus;
  };

  const getGoalText = (goal: string | null) => {
    if (!goal) return 'Не указано';
    switch (goal) {
      case 'sales': return 'Продажи';
      case 'leads': return 'Лиды';
      case 'awareness': return 'Узнаваемость';
      default: return goal;
    }
  };

  const getGeoText = (geo: string | null) => {
    if (!geo) return 'Не указано';
    switch (geo) {
      case 'moscow': return 'Москва';
      case 'spb': return 'Санкт-Петербург';
      case 'russia': return 'Россия';
      default: return geo;
    }
  };


  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 2, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#4d648d' }} />
        <Typography variant="body2" sx={{ color: '#000000', mt: 2 }}>
          Загружаем кампанию...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleGoBack} variant="contained" sx={{ color: '#ffffff !important' }}>
          Вернуться к списку
        </Button>
      </Container>
    );
  }

  if (!campaign) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Кампания не найдена
        </Alert>
        <Button onClick={handleGoBack} variant="contained" sx={{ color: '#ffffff !important' }}>
          Вернуться к списку
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Header with back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
      }}>
        <Button
          onClick={handleGoBack}
          sx={{ 
            minWidth: 48, 
            width: 48, 
            height: 48, 
            color: '#000000',
            p: 0,
            mr: 2,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            },
            '&:focus': {
              backgroundColor: 'transparent'
            },
            '&:focus-visible': {
              backgroundColor: 'transparent',
              outline: 'none'
            }
          }}
        >
          <ArrowBack />
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000', flexGrow: 1 }}>
          Детали кампании
        </Typography>
      </Box>

      {/* Уведомления о статусе модерации */}
      {moderationSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 600
            }
          }}
        >
          🎉 Объявления успешно отправлены на модерацию в Яндекс.Директ!
        </Alert>
      )}
      
      {moderationError && (
        <Alert 
          severity="error" 
          onClose={() => setModerationError('')}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 600
            }
          }}
        >
          ❌ {moderationError}
        </Alert>
      )}

      {/* Campaign Info Card */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: 'rgb(248, 242, 255)', border: '1px solid #ececfc' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {campaign.name}
            </Typography>
            <CampaignStatusChip 
              status={campaign.status}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>ID:</strong> {campaign.id}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Цель:</strong> {getGoalText(campaign.goal)}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Бюджет:</strong> {campaign.budget ? campaign.budget.toLocaleString('ru-RU') : '0'} ₽
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>География:</strong> {getGeoText(campaign.geo)}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Сайт:</strong> {campaign.campaign_data?.site_url}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Создано:</strong> {new Date(campaign.created_at).toLocaleDateString('ru-RU')}
          </Typography>
        </CardContent>
      </Card>

      {/* Business Profile Card */}
      {campaign.business_profile && (
        <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Портрет бизнеса
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Описание:</strong> {campaign.business_profile.summary}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Источник анализа:</strong> {campaign.business_profile.source === 'ai' ? 'ИИ-анализ' : 'Эвристический анализ'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Язык:</strong> {campaign.business_profile.lang}
            </Typography>

          </CardContent>
        </Card>
      )}

      {/* Campaign Banners Card */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              <Image sx={{ mr: 1, verticalAlign: 'middle' }} />
              Баннеры кампании
            </Typography>
          
          {bannersLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#4d648d', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Загрузка баннеров...
              </Typography>
            </Box>
          ) : banners.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {banners.map((banner, index) => (
                <Card key={banner.id || index} elevation={1} sx={{ bgcolor: '#f8f9fa' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {banner.image_url && (
                        <Box sx={{ flexShrink: 0 }}>
                          <img 
                            src={normalizeImageUrl(banner.image_url)} 
                            alt={banner.title || 'Баннер'}
                            style={{
                              width: '120px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {banner.title || `Баннер ${index + 1}`}
                        </Typography>
                        {banner.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {banner.description}
                          </Typography>
                        )}
                        {banner.concept_name && (
                          <Chip 
                            label={banner.concept_name} 
                            size="small" 
                            sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              {/* Кнопки управления баннерами */}
              {banners.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  {/* Кнопка "Сгенерировать ещё" */}
                  {generatedBannersCount < 3 && (
                    <Button
                      variant="contained"
                      onClick={handleGenerateMoreBanners}
                      disabled={isGeneratingMoreBanners}
                      sx={{
                        background: '#000000',
                        color: '#ffffff !important',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          background: '#333333',
                          opacity: 0.9
                        },
                        '&:disabled': {
                          background: '#f0f0f0',
                          color: '#999999'
                        }
                      }}
                    >
                      {isGeneratingMoreBanners ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                          Генерируем...
                        </Box>
                      ) : (
                        `Сгенерировать ещё (${generatedBannersCount}/3)`
                      )}
                    </Button>
                  )}
                  
                  {/* Блок "Готово к отправке баннеров" или сообщение о том, что баннеры уже заведены */}
                  {hasYandexImageAds ? (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#e8f5e8', 
                      borderRadius: 2, 
                      border: '1px solid #4caf50',
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: '#2e7d32', 
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        Баннеры заведены в Яндекс.Директ
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                        🚀 Готово к отправке в Яндекс.Директ
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Баннеры сгенерированы. Отправьте баннеры на модерацию в Яндекс.Директ.
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={handleSendBannersToModeration}
                          disabled={isSendingToModeration}
                          sx={{
                            background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                            color: '#ffffff !important',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            '&:hover': {
                              background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                              opacity: 0.9
                            },
                            '&:disabled': {
                              background: '#f0f0f0',
                              color: '#999999'
                            }
                          }}
                        >
                          {isSendingToModeration ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                              Отправляем баннеры...
                            </Box>
                          ) : (
                            'Отправить на модерацию'
                          )}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Прогресс модерации */}
                  {isSendingToModeration && (
                    <Box sx={{ width: '100%', maxWidth: 400 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={moderationProgress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)'
                          }
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        {moderationCurrentTask}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Баннеры для этой кампании не найдены
              </Typography>
              {!isGeneratingBanners && banners.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateBanners}
                    disabled={isGeneratingBanners}
                    sx={{
                      background: isGeneratingBanners 
                        ? 'linear-gradient(100deg, #2a2a2a -6.08%, #1a1a1a 25.08%, #0a0a0a)'
                        : 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                      color: '#ffffff !important',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      opacity: isGeneratingBanners ? 0.8 : 1,
                      '&:hover': {
                        background: isGeneratingBanners 
                          ? 'linear-gradient(100deg, #2a2a2a -6.08%, #1a1a1a 25.08%, #0a0a0a)'
                          : 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                        opacity: isGeneratingBanners ? 0.8 : 0.9
                      },
                      '&:disabled': {
                        background: 'linear-gradient(100deg, #2a2a2a -6.08%, #1a1a1a 25.08%, #0a0a0a)',
                        color: '#ffffff !important',
                        opacity: 0.8
                      }
                    }}
                  >
                    {isGeneratingBanners ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                        Генерируем баннеры...
                      </Box>
                    ) : (
                      'Сгенерировать баннеры'
                    )}
                  </Button>
                  {bannerGenerationError && (
                    <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                      {bannerGenerationError}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Прогресс-бар для генерации баннеров */}
              {isGeneratingBanners && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    {bannerCurrentTask}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={bannerProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(bannerProgress)}% завершено
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>


      {/* Campaign Ads Card */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              <TextFields sx={{ mr: 1, verticalAlign: 'middle' }} />
              Рекламные объявления
            </Typography>
          
          {/* Кнопки для показа ключевых слов и минус-слов */}
          {getTextKeywordsAds().length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowKeywords(!showKeywords)}
                sx={{ 
                  fontSize: '0.75rem', 
                  px: 1.5, 
                  py: 0.5,
                  bgcolor: showKeywords ? '#000000' : '#ffffff',
                  color: showKeywords ? '#ffffff' : '#000000',
                  borderColor: '#000000',
                  '&:hover': {
                    bgcolor: showKeywords ? '#333333' : '#f5f5f5',
                    borderColor: '#000000'
                  }
                }}
              >
                Ключевые слова
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowNegatives(!showNegatives)}
                sx={{ 
                  fontSize: '0.75rem', 
                  px: 1.5, 
                  py: 0.5,
                  bgcolor: showNegatives ? '#000000' : '#ffffff',
                  color: showNegatives ? '#ffffff' : '#000000',
                  borderColor: '#000000',
                  '&:hover': {
                    bgcolor: showNegatives ? '#333333' : '#f5f5f5',
                    borderColor: '#000000'
                  }
                }}
              >
                Минус-слова
              </Button>
            </Box>
          )}
          
          {/* Отображение ключевых слов */}
          {showKeywords && getKeywords().length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f0f8ff', borderRadius: 1, border: '1px solid #e3f2fd' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                Ключевые слова ({getKeywords().length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getKeywords().map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    sx={{ fontSize: '0.75rem', bgcolor: '#e3f2fd', color: '#1976d2' }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Отображение минус-слов */}
          {showNegatives && getNegatives().length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f57c00' }}>
                Минус-слова ({getNegatives().length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getNegatives().map((negative, index) => (
                  <Chip
                    key={index}
                    label={negative}
                    size="small"
                    sx={{ fontSize: '0.75rem', bgcolor: '#fff3e0', color: '#f57c00' }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {creativesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#4d648d', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Загрузка объявлений...
              </Typography>
            </Box>
          ) : getTextKeywordsAds().length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Отображаем объявления из объявлений text_keywords */}
              {getTextKeywordsAds().map((ad, index) => (
                <Card key={ad.id || index} elevation={1} sx={{ bgcolor: '#f0f8ff', border: '1px solid #e3f2fd' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {ad.title}
                      </Typography>
                    </Box>
                    
                    {ad.text && (
                      <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                        {ad.text}
                      </Typography>
                    )}
                    
                    {/* Первая ссылка */}
                    {ad.links && ad.links.length > 0 && (
                      <Typography variant="body2" color="primary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        🔗 {ad.links[0].title}: {ad.links[0].url}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Рекламные объявления для этой кампании не найдены
              </Typography>
              {!isGeneratingTextCreatives && getTextKeywordsAds().length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateTextCreatives}
                    disabled={isGeneratingTextCreatives}
                    sx={{
                      background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                      color: '#ffffff !important',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      '&:hover': {
                        background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                        opacity: 0.9
                      },
                      '&:disabled': {
                        background: '#f0f0f0',
                        color: '#999999'
                      }
                    }}
                  >
                    {isGeneratingTextCreatives ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                        Создаем объявления...
                      </Box>
                    ) : (
                      'Создать объявления'
                    )}
                  </Button>
                  {textGenerationError && (
                    <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                      {textGenerationError}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Прогресс-бар для генерации текстовых объявлений */}
              {isGeneratingTextCreatives && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    {textCurrentTask}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={textProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(textProgress)}% завершено
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Кнопка "Отправить на модерацию" или сообщение о том, что объявления уже заведены */}
          {hasYandexTextAds ? (
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: '#e8f5e8', 
              borderRadius: 2, 
              border: '1px solid #4caf50',
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ 
                color: '#2e7d32', 
                fontWeight: 600,
                textAlign: 'center'
              }}>
                Объявления заведены в Яндекс.Директ
              </Typography>
            </Box>
          ) : shouldShowModerationButton() ? (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                🚀 Готово к отправке в Яндекс.Директ
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Рекламные объявления сгенерированы. Отправьте кампанию на модерацию в Яндекс.Директ.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleSendTextAdsToModeration}
                  disabled={isSendingToModeration}
                  sx={{
                    background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                    color: '#ffffff !important',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                      opacity: 0.9
                    },
                    '&:disabled': {
                      background: '#f0f0f0',
                      color: '#999999'
                    }
                  }}
                >
                  {isSendingToModeration ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                      Отправляем текстовые объявления...
                    </Box>
                  ) : (
                    'Отправить на модерацию'
                  )}
                </Button>
              </Box>
            </Box>
          ) : null}

          {/* Прогресс-бар для отправки на модерацию */}
          {isSendingToModeration && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                {moderationCurrentTask}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={moderationProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                    borderRadius: 4
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {Math.round(moderationProgress)}% завершено
              </Typography>
            </Box>
          )}

          {/* Индикатор статуса модерации */}
          {campaign?.status === 'moderation' && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#856404', display: 'flex', alignItems: 'center', gap: 1 }}>
                🔍 Кампания на модерации
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ваша кампания отправлена на модерацию в Яндекс.Директ. Обычно модерация занимает от нескольких минут до нескольких часов.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>




      {/* Statistics Charts */}
      <StatisticsCharts
        campaignId={campaign.id}
        hasYandexCampaign={!!campaign.yandex_campaign_id}
        hasVkCampaign={!!campaign.vk_campaign_id}
      />


      {/* Payment Button */}
      <Button
        variant="contained"
        size="large"
        startIcon={<Payment />}
        onClick={handlePayCampaign}
        fullWidth
        sx={{
          py: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
          color: '#ffffff !important',
          boxShadow: 'none',
          fontWeight: 700,
          mb: 2,
          '&:hover': { 
            background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
            opacity: 0.9,
            boxShadow: 'none'
          }
        }}
      >
        Оплатить кампанию
      </Button>


      <Button 
        variant="outlined" 
        fullWidth 
        onClick={handleGoBack}
        sx={{
          borderColor: '#283655',
          color: '#000000',
          mb: 3,
          '&:hover': {
            borderColor: '#283655',
            backgroundColor: 'rgba(40, 54, 85, 0.04)'
          }
        }}
      >
        Вернуться к списку кампаний
      </Button>
    </Container>
  );
};

export default CampaignDetailPage;

